import React from 'react';
import PageHeader from '../components/PageHeader';
import Callout from '../components/Callout';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiExternalLink, FiRefreshCw, FiAlertTriangle, FiCheckCircle } = FiIcons;

const Evidence = () => {
  const replicationData = [
    { 
      project: "Psychology (OSC)", 
      source: "Science 2015", 
      scope: "100 studies", 
      rate: "36%", 
      finding: "Effect sizes were approximately half of the original reports.",
      link: "https://www.science.org/doi/10.1126/science.aac4716"
    },
    { 
      project: "Experimental Economics", 
      source: "Science 2016", 
      scope: "18 studies", 
      rate: "61%", 
      finding: "Replication effect sizes were ~66% of the original.",
      link: "https://www.science.org/doi/10.1126/science.aaf0918"
    },
    { 
      project: "Social Science (Nature/Science)", 
      source: "Nature Hum. Beh. 2018", 
      scope: "21 studies", 
      rate: "62%", 
      finding: "Significant results found in 13/21; effect sizes ~50% of original.",
      link: "https://www.nature.com/articles/s41562-018-0399-z"
    },
    { 
      project: "Cancer Biology (RP:CB)", 
      source: "eLife 2021", 
      scope: "193 planned / 50 completed", 
      rate: "46%*", 
      finding: "Median replication effect 85% smaller; many lacked data to even attempt.",
      link: "https://elifesciences.org/articles/71601"
    }
  ];

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="prose-custom">
      <PageHeader 
        badge="Context"
        title="Why It Matters: The Evidence" 
        subtitle="Empirical meta-research into the reliability of the scientific record."
      />

      <section className="mb-12">
        <p className="text-lg">
          The conversation around "reproducibility" is often framed as a binary crisis—either science works or it doesn't. 
          In reality, the evidence shows that reliability is <strong>uneven and field-dependent</strong>. While some 
          subfields face significant headwinds, others demonstrate robust self-correction.
        </p>
      </section>

      <h2>The Researcher's Perspective</h2>
      <p>
        In a landmark 2016 Nature survey of 1,576 researchers (<a href="https://www.nature.com/articles/533452a" target="_blank" rel="noopener noreferrer">Baker, 2016</a>), 
        the community reported a complex relationship with their own literature:
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-8">
        <div className="bg-slate-100 p-5 rounded-lg border border-slate-200">
          <div className="text-3xl font-bold text-brand-700 mb-1">&gt;70%</div>
          <p className="text-sm m-0 font-medium">Failed to reproduce another scientist's work.</p>
        </div>
        <div className="bg-slate-100 p-5 rounded-lg border border-slate-200">
          <div className="text-3xl font-bold text-brand-700 mb-1">&gt;50%</div>
          <p className="text-sm m-0 font-medium">Failed to reproduce their own experiments.</p>
        </div>
        <div className="bg-slate-100 p-5 rounded-lg border border-slate-200">
          <div className="text-3xl font-bold text-brand-700 mb-1">52%</div>
          <p className="text-sm m-0 font-medium">Agreed there is a "significant crisis."</p>
        </div>
        <div className="bg-slate-100 p-5 rounded-lg border border-slate-200">
          <div className="text-3xl font-bold text-brand-700 mb-1">&lt;31%</div>
          <p className="text-sm m-0 font-medium">Believe failure to reproduce means the result is false.</p>
        </div>
      </div>

      <h2>Systematic Replication Projects</h2>
      <p>
        Over the last decade, several "Many Labs" style projects have attempted to systematically replicate 
        influential findings. Note that <strong>reproducibility</strong> (same data/code) and 
        <strong>replicability</strong> (new data) are different metrics; the table below focuses primarily 
        on replicability.
      </p>

      <div className="overflow-x-auto my-8">
        <table className="min-w-full border-collapse text-left text-sm">
          <thead>
            <tr className="bg-slate-100 border-b border-slate-200">
              <th className="p-3 font-semibold">Project / Field</th>
              <th className="p-3 font-semibold">Scope</th>
              <th className="p-3 font-semibold">Success Rate</th>
              <th className="p-3 font-semibold">Key Finding</th>
            </tr>
          </thead>
          <tbody>
            {replicationData.map((row, i) => (
              <tr key={i} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                <td className="p-3">
                  <div className="font-bold">{row.project}</div>
                  <div className="text-xs text-slate-500">{row.source}</div>
                </td>
                <td className="p-3">{row.scope}</td>
                <td className="p-3 text-brand-700 font-bold">{row.rate}</td>
                <td className="p-3 leading-relaxed">
                  {row.finding}
                  <a href={row.link} className="inline-block ml-1" target="_blank" rel="noopener noreferrer">
                    <SafeIcon icon={FiExternalLink} className="w-3 h-3 inline" />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-[10px] text-slate-400 italic mt-2">
          * Cancer project rate depends on the definition of success; 46% represents a composite of positive effects.
        </p>
      </div>

      <h2>Emerging Cross-Disciplinary Risks</h2>
      <p>
        It is not just biology and psychology. In <strong>ML-based science</strong>, researchers at Princeton 
        (<a href="https://reproducible.cs.princeton.edu/" target="_blank" rel="noopener noreferrer">Kapoor & Narayanan, 2022</a>) 
        identified errors across ~30 fields affecting hundreds of papers. The primary culprit is often 
        <strong>data leakage</strong>, where information from the test set "leaks" into the training set, 
        creating an illusion of high accuracy that vanishes in the real world.
      </p>

      <Callout type="warning" title="Interpret with Care: The Counter-Argument">
        <p className="mb-3">
          Not all scholars agree that science is in a state of "crisis." Daniele Fanelli 
          (<a href="https://www.pnas.org/doi/10.1073/pnas.1708272114" target="_blank" rel="noopener noreferrer">PNAS 2018</a>) 
          argues that the narrative is overstated:
        </p>
        <ul className="space-y-2 list-disc pl-5">
          <li><strong>Heterogeneity:</strong> Replication rates vary wildly; the literature is not uniformly distorted.</li>
          <li><strong>Definitional Ambiguity:</strong> "Success" in replication is not a binary. If a study finds a 
          smaller effect size but in the same direction, is that a failure or a refinement?</li>
          <li><strong>Self-Correction:</strong> Leaders like Brian Nosek argue that these projects are evidence of 
          science’s unique ability to audit itself and improve over time.</li>
        </ul>
      </Callout>

      <h2>The Structural "Why"</h2>
      <p>
        If the literature is indeed skewed, the cause is rarely fraud (admitted fabrication affects only ~1–2%). 
        Instead, it is a combination of structural incentives:
      </p>
      <ul>
        <li><strong>Publication Bias:</strong> A large majority of published papers report positive findings—rising to ~90% in fields like psychology and psychiatry (<a href="https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0010068" target="_blank" rel="noopener noreferrer">Fanelli 2010</a>)—despite many studies lacking the statistical power to reliably detect effects.</li>
        <li><strong>The Human Cost:</strong> Irreproducible preclinical work misdirects which therapies reach human clinical trials, costing billions of dollars and years of patient hope (<a href="https://www.nature.com/articles/483531a" target="_blank" rel="noopener noreferrer">Begley & Ellis 2012</a>).</li>
      </ul>

      <div className="mt-16 pt-8 border-t border-slate-200">
        <div className="flex items-center justify-between text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiRefreshCw} className="w-3 h-3" />
            <span>Sources last verified against primary literature: July 2026</span>
          </div>
          <div>Every figure links to its primary source.</div>
        </div>
      </div>
    </motion.div>
  );
};

export default Evidence;